import { readonly, ref, shallowRef, triggerRef, watch } from 'vue'

import type { CalendarItemType } from '@/constants/persoplan'

export type JumpTargetType = CalendarItemType | 'resourceAllocation'
type JumpTargetId = `${JumpTargetType}.${number | string}`

export interface JumpTargetBase {
  type: JumpTargetType
  id: number | string
}

interface JumpTarget extends JumpTargetBase {
  scrollIntoView: () => void
}

const jumpTargets = shallowRef(new Map<JumpTargetId, JumpTarget>())
/** Reactive tracker consumers can watch for changes of `jumpTargets` */
const jumpTargetsVersion = ref(0)
const jumpToTargetIdWhenAvailable = ref<JumpTargetId>()

function getMapId(jumpTarget: JumpTargetBase) {
  const id: JumpTargetId = `${jumpTarget.type}.${jumpTarget.id}`
  return id
}

function register(jumpTarget: JumpTarget) {
  const mapId = getMapId({ type: jumpTarget.type, id: jumpTarget.id })
  jumpTargets.value.set(mapId, jumpTarget)
  triggerRef(jumpTargets)
  jumpTargetsVersion.value += 1
}

function unregister(jumpTarget: JumpTargetBase) {
  const mapId = getMapId({ type: jumpTarget.type, id: jumpTarget.id })
  jumpTargets.value.delete(mapId)
  triggerRef(jumpTargets)
  jumpTargetsVersion.value += 1
}

function jumpTo(jumpTarget: JumpTargetBase): void {
  if (!isAvailable(jumpTarget)) {
    throw new Error('jumpTarget is not available')
  }

  const mapId = getMapId({ type: jumpTarget.type, id: jumpTarget.id })
  jumpToId(mapId)
}

function jumpToId(jumpTargetId: JumpTargetId) {
  jumpToTargetIdWhenAvailable.value = undefined

  const target = jumpTargets.value.get(jumpTargetId)
  if (!target) throw new Error('jump target is not available')

  target.scrollIntoView()
}

function jumpToWhenAvailable(jumpTarget: JumpTargetBase) {
  if (isAvailable(jumpTarget)) {
    jumpTo(jumpTarget)
    return
  }

  jumpToTargetIdWhenAvailable.value = getMapId(jumpTarget)
}

function isAvailable(jumpTarget: JumpTargetBase) {
  const mapId = getMapId({ type: jumpTarget.type, id: jumpTarget.id })
  return jumpTargets.value.has(mapId)
}

watch(jumpTargets, () => {
  if (!jumpToTargetIdWhenAvailable.value) return
  if (!jumpTargets.value.has(jumpToTargetIdWhenAvailable.value)) return

  jumpToId(jumpToTargetIdWhenAvailable.value)
})

export function useJumpTargetRegistry() {
  return {
    register,
    unregister,
    jumpTo,
    jumpToWhenAvailable,
    isAvailable,
    registeredTargets: readonly(jumpTargets),
    registeredTargetsVersion: readonly(jumpTargetsVersion),
  }
}
